/**
 * Destructive confirm for deleting a recording session.
 *
 * Deleting purges the S3 video/telemetry footage AND every DB row keyed
 * by the session — irreversible. Presentational only: the caller owns
 * the api.deleteSession call + list refresh via `onConfirm`; this
 * component owns the busy/error UX so both admin surfaces (the Fleet
 * side panel and the full DeviceDetail page) share one dialog.
 */

import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface DeleteSessionDialogProps {
  /** Session being deleted; null = dialog closed. */
  sessionId: string | null;
  segmentCount?: number;
  onClose: () => void;
  /** Runs the actual delete + refresh. Resolves on success. */
  onConfirm: (sessionId: string) => Promise<void>;
}

export const DeleteSessionDialog: React.FC<DeleteSessionDialogProps> = ({
  sessionId,
  segmentCount,
  onClose,
  onConfirm,
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (!sessionId) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(sessionId);
      onClose();
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      setError(
        status === 409
          ? 'This session is still recording — stop it before deleting.'
          : (e as { message?: string })?.message ?? 'Delete failed.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={sessionId != null}
      onOpenChange={(o) => {
        if (!o && !busy) {
          setError(null);
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-status-error">
            <Trash2 className="size-5" />
            Delete session?
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2 text-sm text-fg-secondary">
          <p>
            Permanently delete{' '}
            <span className="font-mono text-fg">{sessionId}</span>
            {segmentCount != null && segmentCount > 0
              ? ` (${segmentCount} segment${segmentCount === 1 ? '' : 's'})`
              : ''}
            ?
          </p>
          <p className="text-fg-muted">
            This removes the video and GPS footage from storage and every
            record tied to this session. It cannot be undone.
          </p>
          {error && <p className="font-medium text-status-error">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={busy}
            className="bg-status-error text-fg-inverse hover:bg-status-error/90"
          >
            {busy ? 'Deleting…' : 'Delete permanently'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteSessionDialog;
